import { SearchPageChrome } from "@/components/search/page-chrome"
import {
  BookOpen,
  CircleCheck,
  ExternalLink,
  FileUser,
  Heart,
  NotebookTabs,
  ShieldCheck,
  Star,
  Trophy,
  User,
  Users,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { convertUrl } from "@/services/utility"
import { Button } from "@/components/ui/button"
import {
  Accordion,
  AccordionContent,
  AccordionTrigger,
  AccordionItem,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"

import { addEducatorToShortlist, useShowTeamProps } from "./page-props"
import { ShortlistMembership } from "@/components/search/shortlists/shortlist-membership"
import { ParseKeys } from "i18next"
import {
  EmbeddedSheetPage,
  SheetPage,
  SheetPageTrigger,
} from "@/components/ui/sheet-page"
import { ActionButton } from "@/components/shared/action-buttons"
import { SearchDetailLink } from "@/components/search/search-return-navigation"
import {
  EducatorReliefListingSheet,
  SchoolReliefListingSheet,
} from "./relief-listing-sheets"

// Each action is offered when the page's capabilities say the user may take it;
// a relief invitation also needs the team to be open to invitations.
function Actions() {
  const { team_profile_uuid, relief_invitable_token, capabilities } =
    useShowTeamProps()
  const { t } = useTranslation("search")

  return (
    <>
      {capabilities.can_apply_to_team && (
        <SheetPage
          href={`/educators/team_applications/new`}
          data={{ team_profile_uuid }}
        >
          <SheetPageTrigger asChild>
            <ActionButton Icon={Users} variant="default">
              {t("show.team.apply")}
            </ActionButton>
          </SheetPageTrigger>
        </SheetPage>
      )}
      {relief_invitable_token &&
        capabilities.educator_relief_invitation_href && (
          <EducatorReliefListingSheet
            href={capabilities.educator_relief_invitation_href}
            relief_invitable_token={relief_invitable_token}
            listing_profile_uuid={team_profile_uuid}
          />
        )}
      {relief_invitable_token && capabilities.school_relief_invitation_href && (
        <SchoolReliefListingSheet
          href={capabilities.school_relief_invitation_href}
          relief_invitable_token={relief_invitable_token}
          listing_profile_uuid={team_profile_uuid}
        />
      )}
      {capabilities.can_shortlist && (
        <EmbeddedSheetPage
          href={`/search/educators/${team_profile_uuid}/shortlist_entries/new`}
          scope={addEducatorToShortlist}
        >
          <SheetPageTrigger asChild>
            <ActionButton Icon={Heart} variant="solid">
              {t("show.shortlist")}
            </ActionButton>
          </SheetPageTrigger>
        </EmbeddedSheetPage>
      )}
    </>
  )
}

export default function Page() {
  const { t } = useTranslation("search")
  const { t: tTags } = useTranslation("listing_profile_tags")
  const { team, shortlists } = useShowTeamProps()

  return (
    <>
      <SearchPageChrome identity={{ name: team.name }} action={<Actions />} />
      <PageBody>
        <ShortlistMembership shortlists={shortlists} />
        <div className="space-y-6">
          {/* Team Header Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {team.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {team.written_comment && (
                <p className="text-gray-700">{team.written_comment}</p>
              )}

              <div className="flex flex-wrap gap-2">
                {team.tags.map((tag, index) => (
                  <Badge key={index}>
                    {tTags(tag as ParseKeys<"listing_profile_tags">)}
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2">
                {team.webpage_url && (
                  <Button variant="link" size="sm" asChild>
                    <a
                      href={team.webpage_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t("show.team.website")}
                    </a>
                  </Button>
                )}
                {team.video_url && (
                  <Button variant="link" size="sm" asChild>
                    <a
                      href={team.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t("show.team.video")}
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Teachers Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t("show.team.teachers")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {team.teachers.map((teacher, index) => (
                  <Card key={index}>
                    <CardHeader className="flex flex-row items-baseline gap-2 p-2">
                      <Avatar>
                        {teacher.photo_id ? (
                          <AvatarImage
                            src={convertUrl(teacher.photo_id)}
                            alt={teacher.name}
                          />
                        ) : null}
                        <AvatarFallback>
                          {teacher.abbr || teacher.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <CardTitle className="truncate text-lg">
                        {teacher.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-2">
                      <span className="inline-flex items-center gap-1">
                        <Star className="size-5 fill-yellow-400 stroke-yellow-600" />
                        {teacher.average_feedback_rating} / 5
                      </span>
                    </CardContent>

                    <CardFooter className="p-2">
                      <Button size="sm" className="w-full" asChild>
                        <SearchDetailLink href={teacher.profile_url}>
                          <FileUser className="mr-2 h-4 w-4" />
                          {t("show.team.teacher_profile")}
                        </SearchDetailLink>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Classes Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                {t("show.team.classes")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {team.classes.map((classItem, index) => (
                  <Card key={index}>
                    <CardHeader className="flex justify-between">
                      <CardTitle
                        className="truncate text-lg"
                        title={classItem.name}
                      >
                        {classItem.name}
                      </CardTitle>
                      <Badge variant="secondary" className="text-xs">
                        {classItem.subject_code}
                      </Badge>
                    </CardHeader>
                    <CardContent className="p-2">
                      <p className="text-sm">{classItem.subject_name}</p>
                    </CardContent>
                    <CardFooter className="p-2">
                      <Button size="sm" className="w-full" asChild>
                        <a href={classItem.details_url}>
                          <NotebookTabs className="h-4 w-4" />
                          {t("show.team.class_details")}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Specialities ({team.specialities.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="w-full">
                {team.specialities.map((speciality, index) => (
                  <AccordionItem key={index} value={`speciality-${index}`}>
                    <AccordionTrigger className="text-left">
                      <div className="flex items-center gap-2">
                        <Trophy className="h-4 w-4" />
                        {speciality.name}
                        <Badge variant="secondary" className="ml-2">
                          {speciality.selected.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 gap-2 pt-2 md:grid-cols-2">
                        {speciality.selected.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            className="flex items-center gap-2 rounded bg-gray-50 p-2"
                          >
                            <CircleCheck className="h-4 w-4 text-green-600" />
                            <span className="text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </PageBody>
    </>
  )
}
